import {
  AccountConfiguration,
  ExternalAccountInvitation,
  AccountsAndGroupsData,
  AddClientRequest,
  ApiGeneralDataResponse,
  ApiResponse,
  ApiTags,
  AutoTradingGetChannelsConfiguration,
  AutoTradingGroupsData,
  CheckAccountStatusRequest,
  CheckBinanceAccountStatus,
  ClientAutoTradingConfiguration,
  ClientChannelConfiguration,
  ClientData,
  ClientsAndGroupRequest,
  ClientStatus,
  ConfigurationExchangeInfo,
  ConfigurationItem,
  CreateAccessTokenRequest,
  CreateAccessTokenResponse,
  CreateAccountFromInviteRequest,
  CreateApiKeysRequest,
  CreateCustomStrategyRequest,
  DeleteCustomStrategyRequest,
  EditClientRequest,
  EditCustomStrategyRequest,
  GetAccountConfigurationRequest,
  GetAuthUrlRequest,
  GetClientChannelConfigurationRequest,
  GetMemberWhitelistIpsRequest,
  GetTradingConfigurationRequest,
  GetWhitelistIpsRequest,
  LiveUpdateChannelsResponse,
  MarketplaceSubGroup,
  SetAccountConfigurationRequest,
  SetTradingConfigurationRequest,
  SubGroupAutoTradingConfiguration,
  ExternalInvitationUserData,
} from "@shared/types";
import { EndpointBuilder } from "@reduxjs/toolkit/query";
import {
  processSubGroupsInfoResponse,
  updateClientsAndGroupsFromLiveSearch,
} from "@shared/features/configuration/utils";
import { SUBSCRIBE_GROUP_REFETCH_ENDPOINTS_LIST } from "@shared/common/constants";
import { convertToObjectsArray } from "@shared/utils/arrays";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { getRawFiltersFromClientsList } from "@shared/features/portfolio/utils";
import { addInviteTokenAuthHeader } from "@shared/utils/misc";

export const configurationEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => {
    return {
      getClientAutoTradingConfig: build.query<
        ClientAutoTradingConfiguration,
        { client_id: number }
      >({
        query: (args) => ({
          url: "/get_client_auto/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ClientAutoTradingConfiguration>
        ) => response.data,
      }),
      getClientChannelsAuto: build.query<
        SubGroupAutoTradingConfiguration[],
        AutoTradingGetChannelsConfiguration | void
      >({
        query: (args) => ({
          url: "/get_client_channels_auto/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<[][]>>
        ) => processSubGroupsInfoResponse(response),
      }),
      getClientFreeChannels: build.query<
        SubGroupAutoTradingConfiguration[],
        AutoTradingGetChannelsConfiguration
      >({
        query: (args) => ({
          url: "/get_client_free_channels/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<[][]>>
        ) => processSubGroupsInfoResponse(response, true),
      }),
      getClientFreeTrialChannels: build.query<
        SubGroupAutoTradingConfiguration[],
        AutoTradingGetChannelsConfiguration
      >({
        query: (args) => ({
          url: "/get_client_free_trial_channels/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<[][]>>
        ) => processSubGroupsInfoResponse(response, true),
      }),
      getExchangesList: build.query<
        ConfigurationExchangeInfo[],
        { nested_exchanges: boolean; authToken?: string } | void
      >({
        query: (args) => {
          return addInviteTokenAuthHeader(
            {
              url: "get_exchanges_list/",
              method: "POST",
              body: args,
            },
            args
          );
        },
        transformResponse: (
          response: ApiResponse<ConfigurationExchangeInfo[]>
        ) => response.data,
      }),
      getClientsList: build.query<
        ClientData[],
        { is_demo_query?: boolean } | void
      >({
        query: (args) => ({
          url: "get_clients_list/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<ClientData[]>>
        ) => {
          return response.data.data;
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          if (args) return;
          try {
            const { data } = await queryFulfilled;
            const filtersData = getRawFiltersFromClientsList(data);
            dispatch(
              portfolioFilters.actions.loadFilters({
                data: filtersData,
              })
            );
          } catch {}
        },
        providesTags: [{ type: ApiTags.CLIENTS, id: "LIST" }],
        keepUnusedDataFor: 0,
      }),
      getClientsAndGroups: build.query<AccountsAndGroupsData, void>({
        query: () => ({
          url: "get_clients_and_groups_data/",
          method: "POST",
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<AccountsAndGroupsData>>
        ) => response.data.data,
        providesTags: [{ type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" }],
      }),
      getAutoTradingGroups: build.query<Set<number>, void>({
        query: () => ({
          url: "get_auto_trading_groups/",
          method: "POST",
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<Set<number>>>
        ) => response.data.data || [],
        providesTags: [{ type: ApiTags.AUTO_TRADING_GROUPS, id: "LIST" }],
      }),
      getAutoTradingGroupsByType: build.query<AutoTradingGroupsData, void>({
        query: () => ({
          url: "get_auto_trading_groups_by_type/",
          method: "POST",
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<AutoTradingGroupsData>>
        ) => response.data.data,
        providesTags: [{ type: ApiTags.AUTO_TRADING_GROUPS, id: "LIST" }],
        keepUnusedDataFor: 0,
      }),
      checkApiKeyExistence: build.query<boolean, { api_key: string }>({
        query: (args) => ({
          url: "check_api_key_existence/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<boolean>>
        ) => response.data.data,
      }),
      addClient: build.mutation<void, AddClientRequest>({
        query: (args) => ({
          url: "add_client/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) => {
          return response
            ? [
                { type: ApiTags.USER, id: "LIST" },
                { type: ApiTags.CLIENTS, id: "LIST" },
                { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
              ]
            : [];
        },
      }),
      changeClientStatus: build.mutation<
        void,
        { client_id: number; status: ClientStatus }
      >({
        query: (args) => ({
          url: "change_client_status/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) =>
          response
            ? [
                { type: ApiTags.CLIENTS, id: "LIST" },
                { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
                { type: ApiTags.USER, id: "LIST" },
                { type: ApiTags.AUTO_TRADING_GROUPS, id: "LIST" },
              ]
            : [],
      }),
      editClient: build.mutation<void, EditClientRequest>({
        query: (args) => ({
          url: "edit_client/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) =>
          response
            ? [
                { type: ApiTags.CLIENTS, id: "LIST" },
                { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
              ]
            : [],
      }),
      copyClient: build.mutation<void, EditClientRequest>({
        query: (args) => ({
          url: "copy_client/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) => {
          return response
            ? [
                { type: ApiTags.CLIENTS, id: "LIST" },
                { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
              ]
            : [];
        },
      }),
      getWhitelistIPs: build.query<string[], GetWhitelistIpsRequest | void>({
        query: (args) => {
          return addInviteTokenAuthHeader(
            {
              url: "get_whitelist_ips/",
              method: "POST",
              body: args,
            },
            args
          );
        },
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<string[]>>
        ) => response.data.data,
      }),
      getMemberWhitelistIps: build.query<
        string[],
        GetMemberWhitelistIpsRequest
      >({
        query: (args) => ({
          url: "get_member_whitelist_ips/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<string[]>>
        ) => response.data.data,
      }),
      activateChannelMonitoring: build.query<void, ClientsAndGroupRequest>({
        query: (args) => ({
          url: "activate_channel_monitoring/",
          method: "POST",
          body: args,
        }),
      }),
      verifyChannelMonitoring: build.query<string, ClientsAndGroupRequest>({
        query: (args) => ({
          url: "verify_channel_monitoring/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: ApiResponse<string>) => response.data,
      }),
      getClientChannelConfigurations: build.query<
        ClientChannelConfiguration[],
        ClientsAndGroupRequest
      >({
        query: (args) => ({
          url: "get_client_channel_configurations/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<
            ApiGeneralDataResponse<ClientChannelConfiguration[]>
          >
        ) => response.data.data,
      }),
      getClientChannelConfiguration: build.query<
        ApiGeneralDataResponse<ConfigurationItem>,
        GetClientChannelConfigurationRequest
      >({
        query: (args) => ({
          url: "get_client_channel_configuration/",
          method: "POST",
          body: { ...args, return_custom_strategy: true },
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<ConfigurationItem>>
        ) => response.data,
      }),
      getTradingConfiguration: build.query<
        ApiGeneralDataResponse<ConfigurationItem>,
        GetTradingConfigurationRequest
      >({
        query: (args) => ({
          url: "get_trading_configuration/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<ConfigurationItem>>
        ) => {
          return response.data;
        },
        providesTags: (_, __, args) => [
          { type: ApiTags.TRADING_CONFIGURATION, id: args.client_id },
        ],
      }),
      liveUpdateChannels: build.query<
        ApiResponse<ApiGeneralDataResponse<LiveUpdateChannelsResponse>>,
        { text_contained: string } | { sub_group_id: number }
      >({
        query: (args) => ({
          url: "live_update_channels/",
          method: "POST",
          body: args,
        }),
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const groupsData = data?.data?.data?.groups_data;
            if (groupsData) {
              updateClientsAndGroupsFromLiveSearch(groupsData, dispatch);
            }
          } catch {}
        },
      }),
      setTradingConfiguration: build.mutation<
        void,
        SetTradingConfigurationRequest
      >({
        query: ({ is_onboarding, ...args }) => ({
          url: "set_trading_configuration/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response, _, args) =>
          response
            ? args.client_ids.map((clientId) => ({
                type: ApiTags.TRADING_CONFIGURATION,
                id: clientId,
              }))
            : [],
      }),
      configurationToDefault: build.mutation<void, { client_id: number }>({
        query: (args) => ({
          url: "configuration_to_default/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response, _, args) =>
          response
            ? [{ type: ApiTags.TRADING_CONFIGURATION, id: args.client_id }]
            : [],
      }),
      getAuthUrl: build.query<string, GetAuthUrlRequest>({
        query: (args) => ({
          url: "get_auth_url/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<string>>
        ) => response.data.data,
      }),
      createAccessToken: build.mutation<
        CreateAccessTokenResponse,
        CreateAccessTokenRequest
      >({
        query: (args) => ({
          url: "create_access_token/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<
            ApiGeneralDataResponse<CreateAccessTokenResponse>
          >
        ) => response.data.data,
      }),
      checkAccountStatus: build.query<
        CheckBinanceAccountStatus,
        CheckAccountStatusRequest
      >({
        query: (args) => ({
          url: "check_account_status/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<
            ApiGeneralDataResponse<CheckBinanceAccountStatus>
          >
        ) => response.data.data,
      }),
      createApiKeys: build.mutation<void, CreateApiKeysRequest>({
        query: (args) => ({
          url: "create_api_keys/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) =>
          response
            ? [
                { type: ApiTags.USER, id: "LIST" },
                { type: ApiTags.CLIENTS, id: "LIST" },
                { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
              ]
            : [],
      }),
      refreshClientKeys: build.mutation<void, { client_id: number }>({
        query: (args) => ({
          url: "refresh_client_keys/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) =>
          response
            ? [
                { type: ApiTags.CLIENTS, id: "LIST" },
                { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
                { type: ApiTags.AUTO_TRADING_GROUPS, id: "LIST" },
              ]
            : [],
      }),
      subscribeToGroupFreeTrial: build.mutation<void, { sub_group_id: number }>(
        {
          query: (args) => ({
            url: "subscribe_to_group_free_trial/",
            method: "POST",
            body: args,
          }),
          invalidatesTags: SUBSCRIBE_GROUP_REFETCH_ENDPOINTS_LIST,
        }
      ),
      subscribeToGroupFreeSubscription: build.mutation<
        void,
        { sub_group_id: number }
      >({
        query: (args) => ({
          url: "subscribe_to_group_free_subscription/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: SUBSCRIBE_GROUP_REFETCH_ENDPOINTS_LIST,
      }),
      unsubscribeFromMarketplaceGroup: build.mutation<
        void,
        { sub_group_id: number }
      >({
        query: (args) => ({
          url: "unsubscribe_from_marketplace_group/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: SUBSCRIBE_GROUP_REFETCH_ENDPOINTS_LIST,
      }),
      createCustomStrategy: build.mutation<void, CreateCustomStrategyRequest>({
        query: (args) => ({
          url: "create_custom_strategy/",
          method: "POST",
          body: args,
        }),
      }),
      editCustomStrategy: build.mutation<void, EditCustomStrategyRequest>({
        query: (args) => ({
          url: "edit_custom_strategy/",
          method: "POST",
          body: args,
        }),
      }),
      deleteCustomStrategy: build.mutation<void, DeleteCustomStrategyRequest>({
        query: (args) => ({
          url: "delete_custom_strategy/",
          method: "POST",
          body: args,
        }),
      }),
      migrateLegacyAutoTrading: build.mutation<
        void,
        { client_id_to_sub_group_ids: Record<number, number[]> }
      >({
        query: (args) => ({
          url: "migrate_legacy_auto_trading/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) =>
          response
            ? [
                { type: ApiTags.ACCOUNTS_AND_GROUPS, id: "LIST" },
                { type: ApiTags.BOTS, id: "LIST" },
                { type: ApiTags.USER, id: "LIST" },
              ]
            : [],
      }),
      getClientChannelsInformation: build.query<
        MarketplaceSubGroup[],
        { client_ids: number[] }
      >({
        query: (args) => ({
          url: "/get_client_channels_information/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<[][]>>
        ) => convertToObjectsArray(response.data.data, response.data.headers),
      }),
      getExchangeChannelsInformation: build.query<
        MarketplaceSubGroup[],
        { exchanges: string[] }
      >({
        query: (args) => ({
          url: "/get_exchange_channels_information/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<[][]>>
        ) => convertToObjectsArray(response.data.data, response.data.headers),
      }),
      getAccountConfiguration: build.query<
        AccountConfiguration,
        GetAccountConfigurationRequest
      >({
        query: (args) => ({
          url: "get_account_configuration/",
          method: "POST",
          body: args,
        }),
        transformResponse: (
          response: ApiResponse<ApiGeneralDataResponse<AccountConfiguration>>
        ) => response.data.data,
        providesTags: (_, __, args) => [
          {
            type: ApiTags.ACCOUNTS_AND_GROUPS,
            id: args.account_id,
          },
        ],
      }),
      setAccountConfiguration: build.mutation<
        void,
        SetAccountConfigurationRequest
      >({
        query: (args) => ({
          url: "set_account_configuration/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (_, __, args) => [
          {
            type: ApiTags.ACCOUNTS_AND_GROUPS,
            id: args.account_id,
          },
        ],
      }),
      createAccountInvite: build.mutation<{ token: string }, { name: string }>({
        query: (args) => ({
          url: "create_account_invite/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: ApiResponse<{ token: string }>) =>
          response.data,
        invalidatesTags: [
          { type: ApiTags.EXTERNAL_ACCOUNT_INVITES, id: "LIST" },
        ],
      }),
      deleteAccountInvite: build.mutation<void, { token: string }>({
        query: (args) => ({
          url: "delete_account_invite/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: [
          { type: ApiTags.EXTERNAL_ACCOUNT_INVITES, id: "LIST" },
        ],
      }),
      getAccountInvites: build.query<ExternalAccountInvitation[], void>({
        query: (args) => ({
          url: "get_account_invites/",
          method: "POST",
          body: args,
        }),
        providesTags: () => [
          { type: ApiTags.EXTERNAL_ACCOUNT_INVITES, id: "LIST" },
        ],
        transformResponse: (
          response: ApiResponse<ExternalAccountInvitation[]>
        ) => response.data,
      }),
      getAccountInvite: build.query<
        ExternalInvitationUserData,
        { authToken: string }
      >({
        query: (args) =>
          addInviteTokenAuthHeader(
            {
              url: "get_account_invite/",
              method: "POST",
              body: args,
            },
            args
          ),
        transformResponse: (
          response: ApiResponse<ExternalInvitationUserData>
        ) => response.data,
      }),
      createAccountFromInvite: build.mutation<
        void,
        CreateAccountFromInviteRequest
      >({
        query: (args) =>
          addInviteTokenAuthHeader(
            {
              url: "create_account_from_invite/",
              method: "POST",
              body: args,
            },
            args
          ),
      }),
    };
  },
};
